<template>
  <div>
    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :watchlist-options="watchlistOptions"
    />

    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6">
        <BreadcrumbMenu/>
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal v-if="showFilters" @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :watchlist-options="watchlistOptions"
          :watchlist-filter.sync="watchlistFilter"
          :start-date.sync="startDate"
          :end-date.sync="endDate"
        />
      </template>
    </filter-modal>

    <Transition name="slide-right-left">
      <div v-if="embedded && isFilterPanelOpen" class="face-filters-panel">
        <div class="face-filters-panel__header">
          <p class="h4 text-capitalize mb-2 text-white">
            {{ $t("messages.info.advanced_search") }}
          </p>
          <b-button variant="link" size="sm" class="text-dark p-0" @click="isFilterPanelOpen = false">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="face-filters-panel__content">
          <filters
            :watchlist-options="watchlistOptions"
            :watchlist-filter.sync="watchlistFilter"
            :start-date.sync="startDate"
            :end-date.sync="endDate"
          />
        </div>
        <div class="face-filters-panel__footer">
          <b-button variant="outline-primary" size="sm" class="text-capitalize" @click="clearEmbeddedFilters">
            {{ $t("messages.info.clear_filters") }}
          </b-button>
          <b-button variant="primary" size="sm" class="text-capitalize" @click="applyEmbeddedFilters">
            {{ $t("search") }} <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <toastification-loading-content ref="loadingToast" />

    <!-- Table Container Card -->
    <b-card-group v-show="!embedded || !isFilterPanelOpen" deck :class="embedded ? 'm-0' : 'mx-2 mt-1'">
    <b-card class="m-0" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            v-if="!embedded"
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              data-cy="per-page-selector"
            />
            <label class="text-capitalize mr-1">{{ $t("table.entries") }}</label>
            <btn-refresh-table
              :fetch-list="refetchData"
            />
          </b-col>

          <!-- Search -->
          <b-col cols="12" :md="embedded ? 12 : 6" v-if="showFilters || embedded">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
                data-cy="search-input"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="showFilters && $can('create', 'OmnicloudFrFaces')"
              >
                <span class="text-nowrap text-capitalize">
                  {{ `${$t("actions.add")} ${$t("omnicloud_fr.face.title")}` }}
                </span>
              </b-button>
              <b-button-group v-if="embedded" class="mr-1">
                <b-button class="buttonFilter cursor-pointer d-flex align-items-center" data-cy="filter-btn"
                  @click="isFilterPanelOpen = true">
                  <b-icon-filter-circle-fill scale="1.1" />
                  <span class="align-middle ml-1 text-capitalize">{{ $t("filters") }}</span>
                </b-button>
                <toggle-clear-filter :filters-count="filtersCount" @clear-search-filters="clearSearchFilters" />
              </b-button-group>
              <btn-refresh-table v-if="embedded" :fetch-list="refetchData" class="mr-1" />
              <b-button
                @click="exportExcel()"
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
                v-b-tooltip.hover
                :title="`${$t('messages.export.title')}`"
              >
                <feather-icon icon="DownloadCloudIcon" size="15"/>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>
        <!-- img -->
        <template #cell(image_url)="data">
          <div class="d-flex justify-content-center align-items-center">
            <img
              :src="data.item.image_url"
              class="img-fluid rounded"
              style="max-width: 250px; max-height: 200px;"
              :alt="data.item.image_url"
            />
          </div>
        </template>
        <!-- active -->
        <template #cell(active)="data">
          <b-badge :variant="data.item.active ? 'success' : 'danger'">
            {{ data.item.active ? 'True' : 'False' }}
          </b-badge>
        </template>
        <!-- comments -->
        <template #cell(comment)="data">
          <span class="faceComment">
            {{ data.item.comment }}
          </span>
        </template>
        <!-- watch_list -->
        <template #cell(lists)="data">
          <b-badge
            class="mb-1 ml-1"
            variant="info"
            v-for="list in data.item.lists"
            :key="list.value+'key'">
            {{ list.label }}
          </b-badge>
        </template>
        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              @click="openEdit(data.item)"
              v-if="$can('update', 'OmnicloudFrFaces')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.edit")
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="confirmDelete(data.item.id)"
              v-if="$can('delete', 'OmnicloudFrFaces')"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.delete")
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="
              d-flex
              align-items-center
              justify-content-center justify-content-sm-start
            "
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="
              d-flex
              align-items-center
              justify-content-center justify-content-sm-end
            "
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
              data-cy="pagination"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BButtonGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, watch  } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import Pagination from "@/components/Pagination.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from '@/components/Filters/ButtonFilters.vue';
import BtnRefreshTable from "@/components/Btn Refresh Table/index.vue";
import ToggleClearFilter from "@/components/Filters/ToggleClearFilter.vue";

export default {
  components: {
    Sidenav,
    Filters,
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BButtonGroup,
    vSelect,
    ToastificationLoadingContent,
    Pagination,
    FilterModal,
    ButtonFilters,
    BtnRefreshTable,
    ToggleClearFilter,
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
    slotFilters: {
      type: Object | Array,
      default: () => ({})
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const isFilterPanelOpen = ref(false);
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const buttonExport = ref(false);
    const loadingToast = ref(null);
    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const action = ref("list");
    const isSearchFace = ref(false);
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        context.parent.$i18n.t("messages.export.download"),
        context.parent.$i18n.t("messages.export.Generating_file"),
        "secondary"
      );
      store
        .dispatch("omnicloud_face/export", {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          tenant: tenantFilter.value,
          action: "export",
        })
        .then((response) => {
          console.log(response);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: context.parent.$i18n.t("messages.error.server_error"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };

    const openSearchFace = () => {
      isSearchFace.value = true;
    };

    const notifications = computed(() => store.getters["grid/getAlarms"]);


    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };
    const { t } = useI18nUtils();
    const toast = useToast();
    const translations = {
      "omnicloud_fr.face.success_delete": t("omnicloud_fr.face.success_delete"),
    };
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      tenantFilter,
      clearSearchFilters,
      filtersCount,
      buildFilters,
      watchlistOptions,
      watchlistFilter,
      startDate,
      endDate,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("omnicloud_face/delete", id)
            .then((response) => {
              toast({
                component: ToastificationContent,
                props: {
                  title: translations["omnicloud_fr.face.success_delete"],
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              self.refetchData();
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const applyEmbeddedFilters = () => {
      buildFilters();
      isFilterPanelOpen.value = false;
    };

    const clearEmbeddedFilters = () => {
      clearSearchFilters();
      isFilterPanelOpen.value = false;
    };

    const prettyPrintFeatures = (features) => {
      try {
        // Parse if it's a string, otherwise use as is
        const obj = typeof features === 'string' ? JSON.parse(features) : features;
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return features; // fallback to raw if parsing fails
      }
    };
    const getFeatures = (item) => {
      const features = JSON.parse(item);
      const hasBeard = features?.beard.confidence >= 0.5;
      const age = features?.age;
      const emotion = features?.emotions.name;
      const gender = features?.gender.name;
      const glasses = features?.glasses.name;
      const medmask = features?.medmask.name;
      const response = `
      <ul>
        <li>Beard: ${hasBeard ? 'Yes' : 'No'}</li>
        <li>Age: ${age}</li>
        <li>Emotion: ${emotion}</li>
        <li>Gender: ${gender}</li>
        <li>Glasses: ${glasses}</li>
        <li>Medical Mask: ${medmask}</li>
      </ul>`;
              return response;
      };

    watch(
      () => {
        const f = props.slotFilters?.filters ?? props.slotFilters
        return f && Object.keys(f).length ? f : null
      },
      (val) => {
        if (!val) return
        const f = val
        watchlistFilter.value = f.watchlistFilter || null
        startDate.value = f.startDate || null
        endDate.value = f.endDate || null
      },
      {
        immediate: true,
        deep: true
      }
    )

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      notifications,
      buttonExport,
      loadingToast,
      action,
      exportExcel,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      tenantFilter,
      update,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      watchlistOptions,
      watchlistFilter,
      isSearchFace,
      openSearchFace,
      prettyPrintFeatures,
      getFeatures,
      startDate,
      endDate,
      isFilterPanelOpen,
      applyEmbeddedFilters,
      clearEmbeddedFilters,
    };
  },
};
</script>

<style>
.metadata-scrollable {
  max-height: 240px;
  overflow: auto;
  background: #848f9c;
  color: #fff;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.85rem;
}
.metadata-scrollable-large {
  max-height: 350px;
  font-size: 1.1rem;
}

.metadata-col {
  min-width: 320px;
  width: 320px;
  max-width: 480px;
}
.faceComment {
  display: -webkit-box;
  -webkit-line-clamp: 20;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: pre-line;
}

.face-filters-panel {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.face-filters-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.face-filters-panel__content {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem;
}

.face-filters-panel__footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.dark-layout {
  .face-filters-panel {
    background-color: #283046;
    color: #b4b7bd;
  }

  .face-filters-panel__header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  .face-filters-panel__footer {
    border-top-color: #3b4253;
  }
}
</style>
